import { PlatformSiteAri } from '@atlassian/ari';
import { getGoalSiteAri } from '@atlassian/projects-goals-common/get-app-site-ari';
import type { PreloadedQuery } from 'react-relay';

import { createRelayResource } from '@townsquare/relay-utils';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import type { RouteResource } from '@townsquare/router/resources/types';
import type { ResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import { type AggGoalTabName, DEFAULT_TAB, isAggGoalTab } from '../agg-goal-tabs';
import AggGoalTabContentQuery, {
	type AggGoalTabContentQuery as AggGoalTabContentQueryType,
} from '../components/__generated__/AggGoalTabContentQuery.graphql';

const getTabBooleans = (tabName: AggGoalTabName) => ({
	isUpdatesTab: tabName === 'updates',
	isJiraAlignTab: tabName === 'jiraAlign',
	isJiraTab: tabName === 'jira',
	isProjectsTab: tabName === 'projects',
	isLearningsTab: tabName === 'learnings',
	isRisksTab: tabName === 'risks',
	isDecisionsTab: tabName === 'decisions',
});

const createPlatformSiteAri = (siteId: string): string => {
	try {
		return PlatformSiteAri.create({ siteId }).toString();
	} catch {
		// No valid Platform site ari
		throw new Error('No Platform Site Ari');
	}
};

export const aggGoalViewTabResource: RouteResource<
	PreloadedQuery<AggGoalTabContentQueryType, Record<string, unknown>>
> = createRelayResource<AggGoalTabContentQueryType>({
	getQuery: (routerContext, resourceContext) => {
		const context = resourceContext as ResourceContext;

		const rawCloudId = context.workspace?.cloudId;

		if (!rawCloudId) throw new Error('No cloudId available');

		const containerId = getGoalSiteAri(rawCloudId);
		const queryContext = createPlatformSiteAri(rawCloudId);

		const tabParam = routerContext.match.params.tabName;
		const currentTab: AggGoalTabName = tabParam && isAggGoalTab(tabParam) ? tabParam : DEFAULT_TAB;

		return {
			query: AggGoalTabContentQuery,
			environment: getAggRelayEnvironment(),
			variables: {
				goalKey: routerContext.match.params.id ?? '',
				queryContext: queryContext,
				containerId,
				includeDraftUpdate: fg('townsquare-ai-updates-drafts-on-agg'),
				...getTabBooleans(currentTab),
			},
		};
	},
});
